import React, { createContext, useContext, useCallback } from 'react';

const TimeContext = createContext();

// HARDCODED IST timezone — overrides container/server timezone
const IST_TIMEZONE = 'Asia/Kolkata';

export const useTime = () => {
  const context = useContext(TimeContext);
  if (!context) {
    throw new Error('useTime must be used within a TimeProvider');
  }
  return context;
};

export const TimeProvider = ({ children }) => {
  const getCurrentTime = useCallback(() => {
    return new Date();
  }, []);

  const formatDate = useCallback((date, options = {}) => {
    if (!date) return '-';
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '-';
    return dateObj.toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      timeZone: IST_TIMEZONE,
      ...options
    });
  }, []);

  const formatDateTime = useCallback((date, options = {}) => {
    if (!date) return '-';
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '-';
    return dateObj.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
      timeZone: IST_TIMEZONE,
      ...options
    });
  }, []);

  const formatTime = useCallback((date, options = {}) => {
    if (!date) return '-';
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '-';
    return dateObj.toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', hour12: true,
      timeZone: IST_TIMEZONE,
      ...options
    });
  }, []);

  const getTimeAgo = useCallback((date) => {
    if (!date) return '-';
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '-';
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hr ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return formatDate(date);
  }, [formatDate]);

  const getTimezoneName = useCallback(() => IST_TIMEZONE, []);

  const value = {
    getCurrentTime, formatDate, formatDateTime, formatTime,
    getTimeAgo, getTimezoneName, isTimeSynced: true
  };

  return (
    <TimeContext.Provider value={value}>
      {children}
    </TimeContext.Provider>
  );
};

export default TimeContext;
